import { useState } from "react";
import {
  PROMOTION_KIND_LABELS,
  promotionVisibilityLabel,
  type PromotionDto,
  type PromotionInput,
} from "@school-sales-ai/shared";
import { PromotionEditorDialog } from "@/components/promotions/PromotionEditorDialog";
import { describeWindow } from "@/components/promotions/promotion-form";
import { useCatalogQuery } from "@/hooks/useCatalog";
import {
  useCreatePromotion,
  usePromotions,
  useSetPromotionActive,
  useUpdatePromotion,
} from "@/hooks/usePromotions";
import { formatMxn } from "@/lib/format-mxn";

type EditorState = { open: boolean; promotion: PromotionDto | null };

/** What the promotion does to a price, in one cell. */
function adjustmentLabel(p: PromotionDto, currency: string): string {
  if (p.kind === "percent_off") {
    return p.percentOffBp === null ? "—" : `−${p.percentOffBp / 100}%`;
  }
  return formatMxn(p.overridePriceCents, currency);
}

export function Promociones(): React.JSX.Element {
  const [editor, setEditor] = useState<EditorState>({ open: false, promotion: null });

  const { data: promotions, isPending, isError, error } = usePromotions();
  const { data: catalog } = useCatalogQuery();
  const create = useCreatePromotion();
  const update = useUpdatePromotion();
  const setActive = useSetPromotionActive();

  // A catalog row has no id until it has been SAVED (`rowId` in the shared
  // schema is optional for exactly that reason). An unsaved product cannot be
  // promoted — there is nothing for `promotion_products` to point at — so it is
  // filtered out rather than rendered as an option that would fail on submit.
  const products = (catalog?.draft.products ?? []).flatMap((p) =>
    p.id === undefined ? [] : [{ id: p.id, name: p.name, priceCents: p.priceCents }],
  );
  const currency = catalog?.draft.currency ?? "MXN";

  const save = (input: PromotionInput): void => {
    const onDone = { onSuccess: () => setEditor({ open: false, promotion: null }) };
    if (editor.promotion) {
      update.mutate({ id: editor.promotion.id, input }, onDone);
    } else {
      create.mutate(input, onDone);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink-900">Promociones</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-600">
            Cambia el precio de ciertos productos durante un rango de fechas, ciertos días
            o ciertas horas. Puedes elegir si el agente le menciona la promoción al cliente
            o simplemente le da el precio nuevo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditor({ open: true, promotion: null })}
          className="rounded-md bg-ink-900 px-3 py-2 text-sm text-white"
        >
          Nueva promoción
        </button>
      </div>

      {isError ? (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      {isPending ? (
        <p className="mt-6 text-sm text-ink-500">Cargando…</p>
      ) : (promotions ?? []).length === 0 ? (
        <p className="mt-6 text-sm text-ink-500">
          Todavía no hay promociones. Crea una para cambiar el precio de uno o varios
          productos durante cierto tiempo.
        </p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[840px] text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="py-2 pr-3 font-medium">Promoción</th>
                <th className="py-2 pr-3 font-medium">Precio</th>
                <th className="py-2 pr-3 font-medium">Cuándo</th>
                <th className="py-2 pr-3 font-medium">Productos</th>
                <th className="py-2 pr-3 font-medium">El cliente</th>
                <th className="py-2 pr-3 font-medium">Estado</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {(promotions ?? []).map((p) => (
                <tr key={p.id} className="border-b border-ink-100 align-top">
                  <td className="py-3 pr-3">
                    <div className="font-medium text-ink-900">{p.name}</div>
                    <div className="text-xs text-ink-500">{PROMOTION_KIND_LABELS[p.kind]}</div>
                  </td>
                  <td className="py-3 pr-3 text-ink-800">{adjustmentLabel(p, currency)}</td>
                  <td className="py-3 pr-3 text-ink-700">{describeWindow(p)}</td>
                  <td className="py-3 pr-3 text-ink-700">
                    {p.products.map((x) => x.name).join(", ")}
                  </td>
                  <td className="py-3 pr-3 text-ink-700">
                    {promotionVisibilityLabel(p.announce)}
                  </td>
                  <td className="py-3 pr-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        p.isActive
                          ? "bg-lime-100 text-lime-900"
                          : "bg-ink-100 text-ink-600"
                      }`}
                    >
                      {p.isActive ? "Activa" : "Apagada"}
                    </span>
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditor({ open: true, promotion: p })}
                      className="rounded-md border border-ink-200 px-2.5 py-1 text-xs text-ink-700"
                    >
                      Editar
                    </button>{" "}
                    {/* Switched off, never deleted: an order line records which
                        promotion set its price, and those are financial records. */}
                    <button
                      type="button"
                      disabled={setActive.isPending}
                      onClick={() => setActive.mutate({ id: p.id, isActive: !p.isActive })}
                      className="rounded-md border border-ink-200 px-2.5 py-1 text-xs text-ink-700 disabled:opacity-50"
                    >
                      {p.isActive ? "Apagar" : "Encender"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor.open ? (
        <PromotionEditorDialog
          promotion={editor.promotion}
          products={products}
          currency={currency}
          saving={create.isPending || update.isPending}
          onCancel={() => setEditor({ open: false, promotion: null })}
          onSave={save}
        />
      ) : null}
    </div>
  );
}
